import { observer } from 'mobx-react-lite'
import { CheckCircleIcon } from '@heroicons/react/24/solid'
import { Link, useNavigate } from 'react-router-dom'
import { Plus as PlusIcon } from 'iconoir-react'
import { MapPin, WarningCircle, XmarkCircle } from 'iconoir-react'
import { ReactElement, useMemo } from 'react'
import { ColumnDef } from '@tanstack/react-table'
import { Button } from 'app/components/ui/button'
import { PageScroll } from 'app/components/layout/PageScroll'
import { PageHeader } from 'app/components/ui/page-header'
import { AnchorCell } from 'app/components/table/AnchorCell'
import SimpleTable from 'app/components/table/SimpleTable'
import { TableEmptyState } from 'app/components/ui/table-empty-state'
import { LocationJson } from 'app/types/entities'
import { businessDaysDiff } from 'lib/businessDays'
import type { UseLocationSettingsReturn } from './useLocationSettings'

export type LocationSettingsShadcnComponentProps = UseLocationSettingsReturn

function PMSCell({ isIntegrated, lastPmsSyncDate }: { isIntegrated: boolean; lastPmsSyncDate?: string }) {
  if (!isIntegrated) return <XmarkCircle className="h-5 w-5 text-red-400" />
  if (lastPmsSyncDate && Math.abs(businessDaysDiff(new Date(lastPmsSyncDate), new Date())) <= 2) {
    return <CheckCircleIcon className="h-5 w-5 text-blue-400" />
  }
  return <WarningCircle className="h-5 w-5 rounded-full text-amber-900" />
}

export const LocationSettingsShadcnComponent = observer(function LocationSettingsShadcnComponent({
  locations,
  listError,
  canCreateLocation,
}: LocationSettingsShadcnComponentProps): ReactElement {
  const hits = locations?.hits ?? []
  const navigate = useNavigate()

  const columns = useMemo(
    (): ColumnDef<LocationJson, any>[] => [
      {
        id: `name`,
        accessorKey: `name`,
        header: `Location Name`,
        meta: {
          defaultVisible: true,
          ControlledComponent: (row) => {
            // Absolute: AnchorCell renders a plain <a>, which resolves a relative href against the URL without a trailing slash.
            const href = `/settings/location-settings/${row?.id}/general`
            return (
              <AnchorCell href={href} tooltipMessage={row?.name} clickHandler={() => navigate(href)}>
                <span className="font-medium text-strong">{row?.name}</span>
              </AnchorCell>
            )
          },
        },
      },
      {
        id: `tags`,
        accessorKey: `tags`,
        header: `Tags`,
        cell: (props) => (
          <div className="flex flex-wrap gap-1">
            {(props.getValue() ?? []).map((tag: string) => (
              <span
                key={tag}
                className="inline-flex items-center rounded-md bg-surface-muted px-2 py-0.5 text-xs font-medium text-default"
              >
                {tag}
              </span>
            ))}
          </div>
        ),
        meta: { defaultVisible: true },
      },
      {
        id: `pmsIntegrated`,
        accessorKey: `pmsIntegrated`,
        header: `PMS Integration`,
        cell: (props) => (
          <PMSCell
            isIntegrated={!!props.getValue()}
            lastPmsSyncDate={props.row.original.lastPmsSyncDate as unknown as string}
          />
        ),
        meta: { defaultVisible: true },
      },
      {
        id: `state`,
        accessorKey: `state`,
        header: `State`,
        meta: { defaultVisible: true },
      },
      {
        id: `city`,
        accessorKey: `city`,
        header: `City`,
        meta: { defaultVisible: true },
      },
      {
        id: `address`,
        accessorKey: `address`,
        header: `Address`,
        meta: { defaultVisible: true },
      },
    ],
    [navigate],
  )

  return (
    <PageScroll
      variant="table"
      header={
        <PageHeader
          title="Locations"
          totalItems={locations?.total}
          primaryButtonSlot={
            canCreateLocation ? (
              <Link to="new">
                <Button variant="primary" size="sm">
                  <PlusIcon className="h-4 w-4" />
                  <span>Add Location</span>
                </Button>
              </Link>
            ) : undefined
          }
        />
      }
    >
      {listError && !locations ? (
        <div className="py-5 text-center text-red-600">Failed to load locations. Please try again later.</div>
      ) : (
        <SimpleTable
          columns={columns}
          data={locations ? hits : undefined}
          hideFooter
          edgePadded
          emptyState={
            <TableEmptyState
              icon={<MapPin className="h-6 w-6" strokeWidth={1.5} />}
              title="No locations yet"
              description="Each location gets its own phone numbers, hours, and product settings."
              action={
                canCreateLocation ? (
                  <Link to="new">
                    <Button variant="primary" size="sm">
                      <PlusIcon className="h-4 w-4" />
                      <span>Add Location</span>
                    </Button>
                  </Link>
                ) : undefined
              }
            />
          }
        />
      )}
    </PageScroll>
  )
})
